import { useState } from "react"

import {
  mapCreateTestSetInputToTransport,
  useCreateTestSetTransport,
} from "@/test-set/transport"
import { toast } from "@coval/ui"

import { logger } from "@/lib/logger"
import type { JsonFieldValue } from "@/types/json"
import type { RunRowLike } from "@/types/RunRow"
import type { TestCaseInput } from "@/types/TestCaseInput"
import { TestSetType } from "@/types/TestSetInput"

const log = logger("useRunResultsTestSetGeneration")

interface UseRunResultsTestSetGenerationParams {
  getTestSetHref: (testSetId: string) => string
  scopedRunRows: RunRowLike[]
  selectedSimulationOutputIds: string[]
}

function toTestCaseExpectedOutput(
  value: JsonFieldValue | undefined,
): TestCaseInput["expected_output_json"] {
  if (value == null) {
    return null
  }

  return value as TestCaseInput["expected_output_json"]
}

export function useRunResultsTestSetGeneration({
  getTestSetHref,
  scopedRunRows,
  selectedSimulationOutputIds,
}: UseRunResultsTestSetGenerationParams) {
  const [isOpen, setIsOpen] = useState(false)
  const createTestSetMutation = useCreateTestSetTransport()

  const open = () => {
    if (selectedSimulationOutputIds.length > 0) {
      setIsOpen(true)
    }
  }

  const close = () => setIsOpen(false)

  const confirm = async () => {
    if (selectedSimulationOutputIds.length === 0) {
      return
    }

    const selectedSimulationOutputIdSet = new Set(selectedSimulationOutputIds)
    const selectedTestCases = scopedRunRows
      .filter(
        (row) =>
          row.simulation_output_id != null &&
          selectedSimulationOutputIdSet.has(row.simulation_output_id),
      )
      .map((row) => ({
        input_str: row.input_str ?? "",
        expected_behaviors: row.expected_behaviors ?? [],
        expected_output_json: toTestCaseExpectedOutput(
          row.expected_output_json,
        ),
        s3_bucket: row.simulation_s3_bucket ?? null,
        s3_key: row.simulation_s3_key ?? null,
        input_type: TestSetType.DEFAULT,
        simulation_metadata_input: null,
        metric_input: null,
      }))
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, "")

    try {
      const newTestSet = await createTestSetMutation.mutateAsync(
        mapCreateTestSetInputToTransport({
          testSetInput: {
            display_name: `Rerun of ${selectedSimulationOutputIds.length} test cases (${timestamp})`,
            description: `Test set created from rerun of ${selectedSimulationOutputIds.length} selected test cases`,
            test_set_type: TestSetType.DEFAULT,
            testCases: selectedTestCases,
            test_set_metadata: {},
          },
        }),
      )
      toast.info({
        title: "Success",
        description: "New test set created successfully",
        duration: 6000,
      })
      window.open(getTestSetHref(newTestSet.test_set_id), "_blank")
    } catch (error) {
      log.error("Error creating test set", error)
      toast.error({
        title: "Error",
        error,
        fallback: "Failed to create test set.",
        duration: 6000,
      })
    } finally {
      setIsOpen(false)
    }
  }

  return {
    close,
    confirm,
    isOpen,
    isPending: createTestSetMutation.isPending,
    open,
  }
}
